import classNames from 'classnames'
import { type Dispatch, type SetStateAction } from 'react'

import { Icon } from '@/shared'
import { type ISwapSequencs } from '@App/types/common'
import { EIcons } from '@App/types/iconType'
import {
    type TMainCategoryData,
    type IServiceDec,
    type IChildServiceDec,
    type ICategoryDto
} from '@App/types/servicesTypes'
import { OTHERS_CATEGORY_ID } from '@Config/config'

import { Services } from '../../Services/Services'
import { Sections } from '../Sections'

import { EmptyList } from './EmptyList/EmptyList'
import { NameTitle } from './NameTitle/NameTitle'
import style from './SectionItem.module.scss'

interface IProps {
    item: IServiceDec
    chosenSection: string | null
    editableSection: TMainCategoryData | null
    parentId: string
    // adminRoles?: IRole[]
    highlited: string | null
    onSwapCategories: (dto: ISwapSequencs) => void
    onSwapServices: (dto: ISwapSequencs) => void
    onGetSectionsRules: (id: string) => void
    setEditableSection: Dispatch<SetStateAction<TMainCategoryData | null>>
    setRemovableSection: (removableSection: TMainCategoryData | null) => void
    setChosenSection: (chosenSection: string | null) => void
    onSetTranferSevice: (tranferSevice: IChildServiceDec | null) => void
    setAddSectionId: (addSectionId: string | null) => void
    onRemoveSection: (id: string, onSuccess: () => void) => void
    onEditSection: (dto: ICategoryDto, onSuccess: () => void) => void
    onAddSection: (dto: ICategoryDto, onSuccess: () => void) => void

    onDragStartHandler: (e: any, item: IServiceDec) => void
    onDragLeaveHandler: (e: any, id: string) => void
    onDragEndHandler: (e: any) => void
    onDragOverHandler: (e: any, id: string) => void
    onDropHandler: (e: any, item: IServiceDec) => void
}

export const SectionItem = (props: IProps) => {
    const {
        item,
        chosenSection,
        editableSection,
        parentId,
        // adminRoles,
        highlited,
        onSwapCategories,
        onSwapServices,
        onGetSectionsRules,
        setChosenSection,
        setRemovableSection,
        setEditableSection,
        onSetTranferSevice,
        setAddSectionId,
        onRemoveSection,
        onEditSection,
        onAddSection,
        onDragEndHandler,
        onDragLeaveHandler,
        onDragOverHandler,
        onDragStartHandler,
        onDropHandler
    } = props

    const { childCategories, childServices, name, id, deepness, isProtected } = item

    const isEdit = (editableSection && editableSection.id === id) || false

    const containerCn = classNames(
        style.container,
        chosenSection !== id && highlited === id && style.highlitedContainer,
        isEdit && style.editableItemContainer
    )

    const onSetChosenSection = () => {
        if (chosenSection && chosenSection === id) {
            setChosenSection(null)
        } else {
            setChosenSection(id)
        }
    }

    const onSaveEditedSection = () => {
        if (editableSection?.name) {
            const dto: ICategoryDto = {
                parentId,
                id,
                name: editableSection.name,
                isBarsFiltering: false,
                preparingDescription: null,
                exceptionRules: [],
                permissionRules: []
            }
            onEditSection(dto, () => setChosenSection(null))
        }
    }

    const onRemoveSectionHandler = () => {
        setRemovableSection({ id, name })
    }

    const onEditItem = () => {
        if (editableSection) {
            setEditableSection(null)
        } else {
            setEditableSection({ id, name })
        }
    }

    const onGetCategoryRulesHandler = () => {
        onGetSectionsRules(id)
    }

    const onRenderCategories = () => {
        switch (true) {
            case childCategories.length > 0:
                return (
                    <Sections
                        // adminRoles={adminRoles}
                        sections={childCategories}
                        parentName={name}
                        parentId={parentId}
                        onSwapCategories={onSwapCategories}
                        onSwapServices={onSwapServices}
                        onGetSectionsRules={onGetSectionsRules}
                        onSetTranferSevice={onSetTranferSevice}
                        setAddSectionId={setAddSectionId}
                        onRemoveSection={onRemoveSection}
                        onEditSection={onEditSection}
                        onAddSection={onAddSection}
                    />
                )
            case childServices.length > 0:
                return (
                    <Services
                        services={childServices}
                        parentName={name}
                        parentId={id}
                        isDraggable={id !== OTHERS_CATEGORY_ID}
                        onSwapServices={onSwapServices}
                        onSetTranferSevice={onSetTranferSevice}
                    />
                )
            default:
                return (
                    <EmptyList
                        categoryId={id}
                        categoryName={name}
                        setAddSectionId={setAddSectionId}
                        isDeepLevel={(deepness && deepness >= 3) || false}
                    />
                )
        }
    }

    // const isAdditionalMethodAllowed = adminRole === ROLE_MAPPER.SADMN || !isProtected
    const isAdditionalMethodAllowed = !isProtected
    return (
        <div
            className={containerCn}
            onDragStart={(e: any) => onDragStartHandler(e, item)}
            onDragLeave={(e: any) => onDragLeaveHandler(e, id)}
            onDragEnd={onDragEndHandler}
            onDragOver={(e: any) => onDragOverHandler(e, id)}
            onDrop={(e: any) => onDropHandler(e, item)}
            draggable={!chosenSection}
        >
            <div className={style.sectionItem}>
                <NameTitle
                    isEdit={isEdit}
                    name={name}
                    editableSection={editableSection}
                    setEditableSection={setEditableSection}
                />
                {isEdit && (
                    <div className={style.saveIconWrapper} onClick={onSaveEditedSection}>
                        <Icon iconName={EIcons.PLUS} color='#007DEE' />
                    </div>
                )}
                {isAdditionalMethodAllowed && chosenSection !== id && !isEdit && (
                    <div className={style.removeIconWrapper} onClick={onRemoveSectionHandler}>
                        <div>
                            <Icon iconName={EIcons.TRSH2} color='#007DEE' />
                        </div>
                    </div>
                )}
                {isAdditionalMethodAllowed && chosenSection !== id && (
                    <div className={style.editIconWrapper} onClick={onEditItem}>
                        <div>
                            <Icon iconName={isEdit ? EIcons.CLOS : EIcons.PEN} color='#007DEE' />
                        </div>
                    </div>
                )}
                {!isEdit && (
                    <div className={style.noteIcon} onClick={onGetCategoryRulesHandler}>
                        <div>
                            <Icon iconName={EIcons.Note} color='#007DEE' />
                        </div>
                    </div>
                )}
                {!isEdit && (
                    <div className={style.arrowIconWrapper} onClick={onSetChosenSection}>
                        <div className={classNames(style.icon, chosenSection === id && style.activeIcon)}>
                            <Icon iconName={EIcons.AROW} />
                        </div>
                    </div>
                )}
            </div>
            {chosenSection === id && onRenderCategories()}
        </div>
    )
}
